import { FormNotice } from '@zappyrent/hundop';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { upperFirst, upperCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TelephoneInput from '../../../components/___design/TelephoneInput';
import TextInput from '../../../components/___design/TextInput';
import InfoIcon from '../../../components/icons/info-icon';
import { Checkbox_2 } from '../../../components/UI/Checkbox_2';
import { MultipleRadiobutton } from '../../../components/UI/MultiRadiobutton';
import { IRadiobuttonOption } from '../../../components/UI/MultiRadiobutton/MultiRadiobutton.types';
import { NumberInput } from '../../../components/UI/NumberInput/NumberInput';
import { H2Text, H3Text } from '../../../components/UI/Text';
import { YesNoCheckBox } from '../../../components/UI/YesNoCheckbox';
import visitsAPI from '../../../services/api/visits';
import { emailValidationPattern } from '../../../shared/helpers';
import { VisitsQuestionnaireDto } from '../../../types/visits';
import {
  InfoIconContainer,
  StyledButton,
  StyledContainer,
  StyledInputGroupContainer,
  StyledInputGroupRowContainer,
  StyledMainContainer,
  IconContainer,
  InfoContainer,
  TermsContainer,
  TermsText,
} from './VisitQuestionnaireInputs.style';

const ZappyrentSiteURL = process.env.REACT_APP_SITE_URL;
const ZappyrentZendeskSiteURL = `https://zappyrent.zendesk.com`;

type IVisitQuestionnaireFieldsState = {
  errorLabel: string;
  hasErrors: boolean;
  isEditing: boolean;
  isSubmitClicked: boolean;
  label?: string;
  name: keyof VisitsQuestionnaireDto;
} & (
  | {
      type: 'text' | 'email' | 'tel';
      value: string | null | undefined;
    }
  | {
      type: 'yes-no-radio';
      value: boolean | null | undefined;
    }
  | {
      type: 'radiobutton';
      options: IRadiobuttonOption[];
      value: string | null | undefined;
    }
  | {
      type: 'number';
      value: number | null | undefined;
    }
);

const validateField = (field: IVisitQuestionnaireFieldsState): boolean => {
  if (field.type === 'email') {
    const x = !!field?.value?.match(emailValidationPattern);
    return x;
  } else if (field.type === 'tel') {
    return isValidPhoneNumber(field.value || '');
  } else if (field.type === 'text') {
    return (
      field.value !== null &&
      field.value !== undefined &&
      field.value.replace(/\s/g, '') !== ''
    );
  } else if (field.type === 'yes-no-radio' || field.type === 'radiobutton') {
    return field.value !== null && field.value !== undefined;
  } else if (field.type === 'number') {
    return (
      field.value !== null && field.value !== undefined && field.value >= 0
    );
  }
  return false;
};

const VisitQuestionnaireInputs = ({
  visit_id,
  visitsQuestionnaire,
}: {
  visitsQuestionnaire: VisitsQuestionnaireDto;
  visit_id: string;
}): JSX.Element | null => {
  const { t } = useTranslation(['form', 'common']);
  const defaultFormErrorLabel = t('requiredField');
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [fields, setFields] = useState<IVisitQuestionnaireFieldsState[]>([
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      label: 'fullname',
      name: 'tenant_full_name',
      type: 'text',
      value: visitsQuestionnaire.tenant_full_name ?? '',
    },
    {
      errorLabel: t('emailIsNotValid'),
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      label: 'form:email',
      name: 'email',
      type: 'email',
      value: visitsQuestionnaire.email ?? '',
    },
    {
      errorLabel: t('form:phoneIsNotValid'),
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      label: 'form:phoneNumber',
      name: 'phone_no',
      type: 'tel',
      value: visitsQuestionnaire.phone_no ?? '',
    },
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      name: 'number_of_adult_guests',
      type: 'number',
      label: 'form:adults',
      value: visitsQuestionnaire.number_of_adult_guests ?? 0,
    },
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      name: 'number_of_children_guests',
      type: 'number',
      label: 'form:children',
      value: visitsQuestionnaire.number_of_children_guests ?? 0,
    },
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      name: 'has_pets_guests',
      type: 'yes-no-radio',
      value: visitsQuestionnaire.has_pets_guests ?? undefined,
    },
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      name: 'lease_term_request',
      type: 'radiobutton',
      options: [
        {
          label: 'form:rentIntendOptions.for6',
          value: '6_months',
        },
        {
          label: 'form:rentIntendOptions.for12',
          value: '12_months',
        },
        {
          label: 'form:rentIntendOptions.moreThan12',
          value: 'more_than_12_months',
        },
      ],
      value: visitsQuestionnaire.lease_term_request ?? null,
    },
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      name: 'tenant_profession',
      type: 'radiobutton',
      options: [
        {
          label: 'form:professionOptions.unemployed',
          value: 'unemployed',
        },
        {
          label: 'form:professionOptions.selfEmployed',
          value: 'self-employed',
        },
        {
          label: 'form:professionOptions.employee',
          value: 'employed',
        },
        {
          label: 'form:professionOptions.italianStudent',
          value: 'italian_student',
        },
        {
          label: 'form:professionOptions.foreignStudent',
          value: 'foreign_student',
        },
      ],
      value: visitsQuestionnaire.tenant_profession ?? null,
    },
  ]);
  const onClickAcceptTerms = useCallback(() => {
    setIsTermsAccepted(prevState => !prevState);
  }, [setIsTermsAccepted]);

  const handleInputChange = useCallback(
    (name: string, value: string) =>
      setFields(prevFields =>
        prevFields.map(item => {
          if (
            item.name === name &&
            (item.type === 'text' ||
              item.type === 'tel' ||
              item.type === 'email')
          ) {
            const hasErrors = !validateField({ ...item, value });

            return {
              ...item,
              value,
              hasErrors,
            };
          }

          return item;
        }),
      ),
    [setFields],
  );
  const handleYesNoRadioInputChange = useCallback(
    (name: string, value: boolean) =>
      setFields(prevFields =>
        prevFields.map(item => {
          if (item.name === name && item.type === 'yes-no-radio') {
            const hasErrors = !validateField({ ...item, value });
            return {
              ...item,
              value,
              hasErrors,
            };
          }
          return item;
        }),
      ),
    [setFields],
  );
  const handleRadioInputChange = useCallback(
    (name: string, value: string) =>
      setFields(prevFields =>
        prevFields.map(item => {
          if (item.name === name && item.type === 'radiobutton') {
            const hasErrors = !validateField({ ...item, value });
            return {
              ...item,
              value,
              hasErrors,
            };
          }
          return item;
        }),
      ),
    [setFields],
  );
  const handleNumberInputClick = useCallback(
    (name: string, add = true) =>
      setFields(prevFields =>
        prevFields.map(item => {
          if (item.name === name && item.type === 'number') {
            return {
              ...item,
              value: add
                ? (item.value ?? 0) + 1
                : (item.value && item.value > 1 ? item.value : 1) - 1,
            };
          }
          return item;
        }),
      ),
    [setFields],
  );
  const isDisabled = useMemo(
    () => fields.some(field => field.hasErrors),
    [fields],
  );
  const submitForm = useCallback(
    async (visitQuestionnaireFieldsState: IVisitQuestionnaireFieldsState[]) => {
      const body = visitQuestionnaireFieldsState.reduce((prev, currItem) => {
        return {
          ...prev,
          [currItem.name]: currItem.value,
        };
      }, {} as VisitsQuestionnaireDto);
      await visitsAPI.putVisitsQuestionnaire(visit_id, body);
      window.location.replace(
        `${ZappyrentSiteURL}/visits/${visit_id}/schedule`,
      );
    },
    [visit_id],
  );
  const handleContinueClick = useCallback(
    () =>
      setFields(prevFields => {
        let IsNotValid = false;
        const newFields = prevFields.map(item => {
          if (!validateField(item)) {
            IsNotValid = true;
            return {
              ...item,
              hasErrors: true,
            };
          }
          return item;
        });
        if (!IsNotValid) {
          submitForm(newFields);
        }
        return newFields;
      }),
    fields,
  );

  return (
    <StyledMainContainer>
      <StyledContainer>
        <H2Text textAlign="start">
          {t('form:CompleteThisInformationToScheduleYourVisit')}
        </H2Text>
        <StyledInputGroupContainer>
          <H3Text textAlign="start">
            {t('translation:personalInformation')}
          </H3Text>
          <StyledInputGroupRowContainer>
            {fields[0].type === 'text' && (
              <TextInput
                id={fields[0].name}
                label={upperFirst(t(fields[0].label ?? ''))}
                name={fields[0].name}
                type={fields[0].type}
                value={fields[0].value ?? ''}
                onChange={event =>
                  handleInputChange(fields[0].name, event.target.value)
                }
                user="tenant"
              >
                {fields[0].hasErrors && (
                  <FormNotice>{fields[0].errorLabel}</FormNotice>
                )}
              </TextInput>
            )}
          </StyledInputGroupRowContainer>
          <StyledInputGroupRowContainer>
            {fields[1].type === 'email' && (
              <TextInput
                id={fields[1].name}
                label={upperFirst(t(fields[1].label ?? ''))}
                name={fields[1].name}
                type={fields[1].type}
                value={fields[1].value ?? ''}
                onChange={event =>
                  handleInputChange(fields[1].name, event.target.value)
                }
                user="tenant"
              >
                {fields[1].hasErrors && (
                  <FormNotice>{fields[1].errorLabel}</FormNotice>
                )}
              </TextInput>
            )}
            {fields[2].type === 'tel' && (
              <TelephoneInput
                id={fields[2].name}
                label={upperFirst(t(fields[2].label ?? ''))}
                name={fields[2].name}
                value={fields[2].value ?? ''}
                onChange={handleInputChange.bind(undefined, fields[2].name)}
              >
                {fields[2].hasErrors && (
                  <FormNotice>{fields[2].errorLabel}</FormNotice>
                )}
              </TelephoneInput>
            )}
          </StyledInputGroupRowContainer>
        </StyledInputGroupContainer>
        <StyledInputGroupContainer>
          <H3Text textAlign="start" padding="0 0 0.5rem 0">
            {t('form:AmountOfGuests')}
          </H3Text>
          <StyledInputGroupRowContainer>
            {fields[3].type === 'number' && (
              <NumberInput
                value={fields[3].value ?? 0}
                labelText={upperFirst(t(fields[3].label ?? ''))}
                onClickLeft={handleNumberInputClick.bind(
                  undefined,
                  fields[3].name,
                  false,
                )}
                onClickRight={handleNumberInputClick.bind(
                  undefined,
                  fields[3].name,
                )}
              >
                {fields[3].hasErrors && (
                  <FormNotice>{fields[3].errorLabel}</FormNotice>
                )}
              </NumberInput>
            )}
          </StyledInputGroupRowContainer>
          <StyledInputGroupRowContainer>
            {fields[4].type === 'number' && (
              <NumberInput
                value={fields[4].value ?? 0}
                labelText={upperFirst(t(fields[4].label ?? ''))}
                onClickLeft={handleNumberInputClick.bind(
                  undefined,
                  fields[4].name,
                  false,
                )}
                onClickRight={handleNumberInputClick.bind(
                  undefined,
                  fields[4].name,
                )}
              >
                {fields[4].hasErrors && (
                  <FormNotice>{fields[4].errorLabel}</FormNotice>
                )}
              </NumberInput>
            )}
          </StyledInputGroupRowContainer>
        </StyledInputGroupContainer>

        <StyledInputGroupContainer>
          <H3Text textAlign="start">{t('form:AreYouBringingPet')}</H3Text>
          <StyledInputGroupRowContainer>
            {fields[5].type === 'yes-no-radio' && (
              <YesNoCheckBox
                checked={fields[5].value ?? undefined}
                onClick={handleYesNoRadioInputChange.bind(
                  undefined,
                  fields[5].name,
                )}
                user="tenant"
                padding="1rem 0"
              >
                {fields[5].hasErrors && (
                  <FormNotice>{fields[5].errorLabel}</FormNotice>
                )}
              </YesNoCheckBox>
            )}
          </StyledInputGroupRowContainer>
        </StyledInputGroupContainer>
        <StyledInputGroupContainer>
          <H3Text textAlign="start">{t('form:ForHowLongRent')}</H3Text>
          <StyledInputGroupRowContainer>
            {fields[6].type === 'radiobutton' && (
              <MultipleRadiobutton
                activeOption={fields[6].value ?? ''}
                options={fields[6].options.map(option => ({
                  ...option,
                  label: t(option.label),
                }))}
                onChange={handleRadioInputChange.bind(
                  undefined,
                  fields[6].name,
                )}
                user="tenant"
                padding="1rem 0"
              >
                {fields[6].hasErrors && (
                  <FormNotice>{fields[6].errorLabel}</FormNotice>
                )}
              </MultipleRadiobutton>
            )}
          </StyledInputGroupRowContainer>
        </StyledInputGroupContainer>
        <StyledInputGroupContainer>
          <H3Text textAlign="start">{t('form:ChooseYourProfession')}</H3Text>
          {fields[7].type === 'radiobutton' && (
            <>
              <MultipleRadiobutton
                activeOption={fields[7].value ?? ''}
                options={fields[7].options.map(option => ({
                  ...option,
                  label: t(option.label),
                }))}
                onChange={handleRadioInputChange.bind(
                  undefined,
                  fields[7].name,
                )}
                user="tenant"
                padding="1rem 0"
              >
                {fields[7].hasErrors && (
                  <FormNotice>{fields[7].errorLabel}</FormNotice>
                )}
              </MultipleRadiobutton>
              {(fields[7].value === 'unemployed' ||
                fields[7].value === 'self-employed') && (
                <InfoIconContainer>
                  <IconContainer>
                    <InfoIcon />
                  </IconContainer>
                  {t('form:guarantorInfoOption')}
                </InfoIconContainer>
              )}
              <TermsContainer>
                <Checkbox_2
                  label={
                    <InfoContainer>{t('form:questionnaireInfo')}</InfoContainer>
                  }
                  checked={isTermsAccepted}
                  onClick={onClickAcceptTerms}
                  user="tenant"
                  alignItems="top"
                />
              </TermsContainer>
              <H3Text textAlign="start" fontWeight="bolder">
                {t('form:acceptTerms')}
                &nbsp;
                <TermsText
                  href={`${ZappyrentZendeskSiteURL}/${t('form:termsLink')}`}
                >
                  {t('form:termsAndConditions')}
                </TermsText>
              </H3Text>
            </>
          )}
        </StyledInputGroupContainer>
      </StyledContainer>
      <StyledContainer justifyContent="end" padding="0 1rem 2rem 1rem">
        <StyledButton
          onClick={handleContinueClick}
          disabled={isDisabled || !isTermsAccepted}
        >
          {upperCase(t('common:continue'))}
        </StyledButton>
      </StyledContainer>
    </StyledMainContainer>
  );
};
export default VisitQuestionnaireInputs;
